import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { SUBSCRIPTION_STATUS } from '../services/subscriptionApi';
import DeleteWorkspaceModal from '../components/DeleteWorkspaceModal';
import DBCredentialsModal from '../components/DBCredentialsModal';
import AdminPasswordResetModal from '../components/AdminPasswordResetModal';
import CreateWorkspaceModal from '../components/CreateWorkspaceModal';
import BillingAccessModal from '../components/BillingAccessModal';
import AssignBillingProfileModal from '../components/AssignBillingProfileModal';
import TransferWorkspaceModal from '../components/TransferWorkspaceModal';
import CancelTransferModal from '../components/CancelTransferModal';
import {
  Plus,
  Search,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  CreditCard,
  Building2,
  Receipt,
  Users,
  Globe,
  Database,
  KeyRound,
  Trash2,
  FolderOpen,
  ArrowRightLeft,
  AlertTriangle,
  X,
  Shield,
HardDrive,
  Mail,
} from 'lucide-react';

// ============================================
// DESIGN SYSTEM V3 - Clean Table Design
// ============================================

type SortField = 'name' | 'status' | 'subscription' | 'tier';
type SortDirection = 'asc' | 'desc';

const WorkspacesPage: React.FC = () => {
  const navigate = useNavigate();
const { agencyRole, branding, agencyId, serverRegion } = useAuth();
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [selectedWorkspace, setSelectedWorkspace] = useState<any>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
  const [billingAccessModalOpen, setBillingAccessModalOpen] = useState(false);
  const [assignBillingModalOpen, setAssignBillingModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [cancelTransferModalOpen, setCancelTransferModalOpen] = useState(false);
const [cancelTransferData, setCancelTransferData] = useState<{ token: string; workspaceName: string; targetEmail: string } | null>(null);
  const [pendingTransfers, setPendingTransfers] = useState<{ outgoing: any[]; incoming: any[] }>({ outgoing: [], incoming: [] });
  
  // Expiring cards state
  const [expiringCards, setExpiringCards] = useState<{ last4: string; days: number; expired: boolean; profileName: string }[]>([]);
  const [expiryBannerDismissed, setExpiryBannerDismissed] = useState(() => {
    const dismissed = localStorage.getItem('expiryBannerDismissed');
    if (!dismissed) return false;
    // Auto-reset dismissal after 7 days
    const dismissedAt = parseInt(dismissed, 10);
    if (Date.now() - dismissedAt > 7 * 24 * 60 * 60 * 1000) return false;
    return true;
  });

  // Hover states
  const [createBtnHover, setCreateBtnHover] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [hoveredHeader, setHoveredHeader] = useState<SortField | null>(null);

  // Role-based permissions
  const canAccessBilling = agencyRole === 'owner' || agencyRole === 'billing_admin';
  const canCreateWorkspace = agencyRole === 'owner' || agencyRole === 'member';
  const canAccessTechnical = agencyRole === 'owner' || agencyRole === 'member';
  const canManageBillingAdmins = agencyRole === 'owner';

useEffect(() => {
    loadWorkspaces();
    loadPendingTransfers();
    if (canAccessBilling) {
      loadExpiringCards();
    }
  }, [agencyId]);

  const loadExpiringCards = async () => {
    try {
      const response = await apiService.getBillingProfiles();
      const profiles = response.profiles || [];
      const expiring: { last4: string; days: number; expired: boolean; profileName: string }[] = [];
      
      const now = new Date();
      
      profiles.forEach((profile: any) => {
        (profile.paymentMethods || []).forEach((pm: any) => {
          if (!pm.exp_month || !pm.exp_year) return;
          
          const expYear = pm.exp_year < 100 ? 2000 + pm.exp_year : pm.exp_year;
          const expiryDate = new Date(expYear, pm.exp_month, 0);
          const diffTime = expiryDate.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays <= 30) {
            expiring.push({
              last4: pm.last4 || '****',
              days: diffDays,
              expired: diffDays < 0,
              profileName: profile.name,
            });
          }
        });
      });
      
      setExpiringCards(expiring);
    } catch (error) {
      console.error('Failed to load expiring cards:', error);
    }
  };

  const dismissExpiryBanner = () => {
    localStorage.setItem('expiryBannerDismissed', Date.now().toString());
    setExpiryBannerDismissed(true);
  };

  const loadPendingTransfers = async () => {
    try {
      const response = await apiService.get('/transfers/pending');
      setPendingTransfers(response.data || { outgoing: [], incoming: [] });
    } catch (error) {
      console.error('Failed to load pending transfers:', error);
    }
  };

  const getWorkspacePendingTransfer = (workspaceId: number) => {
    return pendingTransfers.outgoing.find((t: any) => t.workspace_id === workspaceId);
  };

  const openCancelTransferModal = (token: string, workspaceName: string, targetEmail: string) => {
    setCancelTransferData({ token, workspaceName, targetEmail });
    setCancelTransferModalOpen(true);
  };

  const handleCancelTransferComplete = () => {
    loadPendingTransfers();
    loadWorkspaces();
  };

useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        // Check if click was on a manage button (they have data-manage-btn attribute)
        const target = event.target as HTMLElement;
        if (!target.closest('[data-manage-btn]')) {
          setMenuOpen(false);
        }
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

const loadWorkspaces = async () => {
    try {
      setLoading(true);
      const data = await apiService.getWorkspaces(agencyId);
setWorkspaces(data);
    } catch (error) {
      console.error('Failed to load workspaces:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sorting logic
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (workspace: any, field: SortField): string => {
    switch (field) {
      case 'name':
        return workspace.name?.toLowerCase() || '';
      case 'status':
        return workspace.status?.toLowerCase() || '';
      case 'subscription':
        return workspace.subscription_status?.toLowerCase() || '';
      case 'tier':
        return workspace.subscription_tier?.toLowerCase() || '';
      default:
        return '';
    }
  };

  // Filter and sort workspaces
  const filteredAndSortedWorkspaces = useMemo(() => {
    const filtered = workspaces.filter(
      (ws) =>
        ws.name.toLowerCase().includes(search.toLowerCase()) ||
        ws.domain.toLowerCase().includes(search.toLowerCase())
    );

    return filtered.sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);
      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [workspaces, search, sortField, sortDirection]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, workspace: any) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 400;
    const viewportHeight = window.innerHeight;
    
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
let top;
    if (spaceBelow < menuHeight && spaceAbove > spaceBelow) {
      top = Math.max(8, rect.top - Math.min(menuHeight, spaceAbove - 8));
    } else {
      top = Math.min(rect.bottom + 4, viewportHeight - menuHeight - 8);
    }

    setMenuPosition({
      top,
      left: rect.right - 240,
    });
    setSelectedWorkspace(workspace);
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  // Row click navigates to subscription page
  const handleRowClick = (workspace: any) => {
    navigate(`/dashboard/workspace/${workspace.id}/subscription`);
  };

  const handleOpenWorkspace = (workspace: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (workspace.deployment_url) {
      window.open(workspace.deployment_url, '_blank');
    }
  };

  // Status dot + text (Better Stack style)
  const getStatusIndicator = (status: string, workspaceId?: number) => {
    // Check if workspace has pending transfer
    const hasPendingTransfer = workspaceId ? getWorkspacePendingTransfer(workspaceId) : false;
    const effectiveStatus = hasPendingTransfer ? 'transfer_pending' : status;

    const config: Record<string, { color: string; label: string }> = {
      active: { color: 'var(--v2-success)', label: 'Active' },
      provisioning: { color: 'var(--v2-warning)', label: 'Provisioning' },
      failed: { color: 'var(--v2-error)', label: 'Failed' },
      deleted: { color: 'var(--v2-text-muted)', label: 'Deleted' },
      transfer_pending: { color: '#f59e0b', label: 'Transfer Pending' },
    };
    const cfg = config[effectiveStatus] || { color: 'var(--v2-text-muted)', label: effectiveStatus };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: cfg.color,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
          {cfg.label}
        </span>
      </div>
    );
  };

  // Subscription status with dot style
  const getSubscriptionIndicator = (workspace: any) => {
    const status = workspace.subscription_status;
    if (!status || status === 'none') return <span style={{ color: 'var(--v2-text-muted)' }}>—</span>;

    const config: Record<string, { color: string; label: string }> = {
      [SUBSCRIPTION_STATUS.TRIAL]: {
        color: '#3b82f6',
        label: workspace.trial_days_remaining !== undefined 
          ? `Trial · ${workspace.trial_days_remaining}d left` 
          : 'Trial',
      },
      [SUBSCRIPTION_STATUS.TRIAL_EXPIRED]: { color: 'var(--v2-error)', label: 'Expired' },
      [SUBSCRIPTION_STATUS.ACTIVE]: { color: 'var(--v2-success)', label: 'Active' },
      [SUBSCRIPTION_STATUS.PAST_DUE]: { color: 'var(--v2-warning)', label: 'Past Due' },
      [SUBSCRIPTION_STATUS.CANCELLED]: { color: 'var(--v2-text-muted)', label: 'Cancelled' },
      [SUBSCRIPTION_STATUS.SUSPENDED]: { color: 'var(--v2-error)', label: 'Suspended' },
      [SUBSCRIPTION_STATUS.PENDING_DELETION]: { color: 'var(--v2-error)', label: 'Pending Delete' },
      [SUBSCRIPTION_STATUS.LIFETIME]: { color: '#8b5cf6', label: 'Lifetime' },
      'transfer_trial': { color: '#f59e0b', label: 'Transfer Trial' },
    };

    const cfg = config[status];
    if (!cfg) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: cfg.color,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
          {cfg.label}
        </span>
      </div>
    );
  };

  // ============================================
  // STYLES
  // ============================================

  const pageStyle: React.CSSProperties = {
    padding: '32px 40px',
    backgroundColor: 'var(--v2-bg)',
    minHeight: '100vh',
    fontFamily: 'var(--font-sans)',
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  };

  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 600,
    color: 'var(--v2-text)',
    letterSpacing: '-0.01em',
  };

  const regionBadgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '0.8125rem',
    fontWeight: 500,
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text-secondary)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-full)',
  };

  const createButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: createBtnHover ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  };

  const searchContainerStyle: React.CSSProperties = {
    position: 'relative',
    maxWidth: '320px',
    marginBottom: '24px',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 14px 10px 40px',
    backgroundColor: 'var(--v2-surface)',
    border: `1px solid ${searchFocused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-lg)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'all 0.15s ease',
    boxShadow: searchFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    overflow: 'hidden',
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const getThStyle = (field: SortField): React.CSSProperties => ({
    padding: '14px 20px',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: sortField === field ? 'var(--v2-text)' : 'var(--v2-text-muted)',
    textAlign: 'left',
    borderBottom: '1px solid var(--v2-border)',
    backgroundColor: 'var(--v2-surface-alt)',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'color 0.15s ease',
  });

  const getTdStyle = (): React.CSSProperties => ({
    padding: '16px 20px',
    borderBottom: '1px solid var(--v2-border-light)',
    verticalAlign: 'middle',
  });

  // Sortable column header component
  const SortableHeader: React.FC<{ field: SortField; label: string; width?: string }> = ({ field, label, width }) => {
    const isActive = sortField === field;
    const isHovered = hoveredHeader === field;
    
    return (
      <th
        style={{ ...getThStyle(field), width }}
        onClick={() => handleSort(field)}
        onMouseEnter={() => setHoveredHeader(field)}
        onMouseLeave={() => setHoveredHeader(null)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>{label}</span>
          <span
            style={{
              display: 'flex',
              flexDirection: 'column',
              opacity: isActive ? 1 : isHovered ? 0.5 : 0,
              transition: 'opacity 0.15s ease',
            }}
          >
            {isActive ? (
              sortDirection === 'asc' ? (
                <ChevronUp size={14} strokeWidth={2} />
              ) : (
                <ChevronDown size={14} strokeWidth={2} />
              )
            ) : (
              <ChevronUp size={14} strokeWidth={1.5} />
            )}
          </span>
        </div>
      </th>
    );
  };

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px' }}>
            <h1 style={titleStyle}>Workspaces</h1>
            <span style={regionBadgeStyle}>
{serverRegion === 'EU' ? '🇪🇺' : '🇺🇸'} {serverRegion === 'EU' ? 'EU' : 'US'} Region
            </span>
          </div>
          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>
            Manage all your {branding?.company_name || 'DashboardFox'} instances
          </p>
        </div>
        {canCreateWorkspace && (
          <button
            onClick={() => setCreateModalOpen(true)}
            onMouseEnter={() => setCreateBtnHover(true)}
            onMouseLeave={() => setCreateBtnHover(false)}
            style={createButtonStyle}
          >
            <Plus size={18} strokeWidth={2} />
            Create Workspace
          </button>
        )}
      </div>

      {/* Incoming Transfers Banner */}
      {pendingTransfers.incoming.length > 0 && (
        <div
          style={{
            padding: '14px 20px',
            backgroundColor: 'var(--v2-info-bg)',
            border: '1px solid #93c5fd',
            borderRadius: 'var(--radius-lg)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ArrowRightLeft size={20} strokeWidth={1.5} color="var(--v2-primary)" />
            <div>
              <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                {pendingTransfers.incoming.length === 1
                  ? `"${pendingTransfers.incoming[0].workspace.name}" is being transferred to you`
                  : `${pendingTransfers.incoming.length} workspaces are being transferred to you`}
              </span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', marginLeft: '8px' }}>
                from {pendingTransfers.incoming[0].sourceUser?.email || 'another user'}
              </span>
            </div>
          </div>
          <button
            onClick={() => {
              const token = pendingTransfers.incoming[0].token;
              window.location.href = `/transfers/accept/${token}`;
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--v2-primary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.8125rem',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            {pendingTransfers.incoming.length === 1 ? 'View Transfer' : 'View Transfers'}
          </button>
        </div>
      )}

{/* Expiring Card Banner */}
      {canAccessBilling && expiringCards.length > 0 && !expiryBannerDismissed && (
        <div
          style={{
            padding: '14px 20px',
            backgroundColor: expiringCards.some(c => c.expired) ? 'var(--v2-error-bg)' : 'var(--v2-warning-bg)',
            border: `1px solid ${expiringCards.some(c => c.expired) ? '#fca5a5' : '#fcd34d'}`,
            borderRadius: 'var(--radius-lg)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle
              size={20}
              strokeWidth={1.5}
              color={expiringCards.some(c => c.expired) ? 'var(--v2-error)' : '#92400e'}
            />
            <div>
              <span
                style={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: expiringCards.some(c => c.expired) ? 'var(--v2-error)' : '#92400e',
                }}
              >
                {expiringCards.some(c => c.expired)
                  ? `Payment method ending in ${expiringCards.find(c => c.expired)?.last4} has expired`
                  : `Payment method ending in ${expiringCards[0].last4} expires in ${expiringCards[0].days} days`}
              </span>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', marginLeft: '8px' }}>
                ({expiringCards[0].profileName})
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => navigate('/dashboard/billing')}
              style={{
                padding: '8px 16px',
                backgroundColor: expiringCards.some(c => c.expired) ? 'var(--v2-error)' : '#f59e0b',
                color: '#ffffff',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Update Now
            </button>
            <button
              onClick={dismissExpiryBanner}
              style={{
                padding: '6px',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Dismiss for 7 days"
            >
              <X size={18} strokeWidth={1.5} color="var(--v2-text-muted)" />
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={searchContainerStyle}>        <Search
          size={18}
          strokeWidth={1.5}
          style={{
            position: 'absolute',
            left: '14px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--v2-text-muted)',
          }}
        />
        <input
          type="text"
          placeholder="Search by name or domain..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          style={searchInputStyle}
        />
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              border: '2px solid var(--v2-border)',
              borderTopColor: 'var(--v2-primary)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        </div>
      ) : filteredAndSortedWorkspaces.length === 0 ? (
        <div style={{ ...cardStyle, padding: '64px', textAlign: 'center' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--v2-primary-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <FolderOpen size={28} strokeWidth={1.5} color="var(--v2-primary)" />
          </div>
          <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)' }}>
            {search ? 'No workspaces found' : 'No workspaces yet'}
          </h3>
          <p style={{ margin: '0 0 20px', fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>
            {search ? 'Try adjusting your search criteria' : 'Create your first workspace to get started'}
          </p>
          {!search && canCreateWorkspace && (
            <button
              onClick={() => setCreateModalOpen(true)}
              style={{
                ...createButtonStyle,
                padding: '10px 24px',
              }}
            >
              <Plus size={18} strokeWidth={2} />
              Create Workspace
            </button>
          )}
        </div>
      ) : (
        <div style={cardStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <SortableHeader field="name" label="Name" />
                <SortableHeader field="status" label="Status" />
                <SortableHeader field="subscription" label="Subscription" />
                <SortableHeader field="tier" label="Tier" />
                <th
                  style={{
                    width: '120px',
                    padding: '14px 16px 14px 20px',
                    backgroundColor: 'var(--v2-surface-alt)',
                    borderBottom: '1px solid var(--v2-border)',
                  }}
                />
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedWorkspaces.map((workspace) => {
                const isHovered = hoveredRow === workspace.id;
                return (
                  <tr
                    key={workspace.id}
                    style={{
                      backgroundColor: isHovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
                      transition: 'background-color 0.1s ease',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={() => setHoveredRow(workspace.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    onClick={() => handleRowClick(workspace)}
                  >
                    {/* Name + Domain (two-line cell) */}
                    <td style={getTdStyle()}>
                      <div style={{ fontWeight: 500, color: 'var(--v2-text)', marginBottom: '2px' }}>
                        {workspace.name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span
                          onClick={(e) => handleOpenWorkspace(workspace, e)}
                          style={{
                            fontSize: '0.8125rem',
                            color: 'var(--v2-primary)',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-mono)',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                        >
                          {workspace.domain}.dashboardfox.app
                        </span>
                        <ExternalLink size={12} strokeWidth={1.5} color="var(--v2-text-muted)" />
                      </div>
                    </td>

                    {/* Status */}
                    <td style={getTdStyle()}>
                      {getStatusIndicator(workspace.status, workspace.id)}
                    </td>

                    {/* Subscription */}
                    <td style={getTdStyle()}>
                      {getSubscriptionIndicator(workspace)}
                    </td>

                    {/* Tier */}
                    <td style={getTdStyle()}>
                      {workspace.subscription_tier && (
                        <span
                          style={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: 'var(--v2-text-tertiary)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em',
                          }}
                        >
                          {workspace.subscription_tier}
                        </span>
                      )}
                    </td>

                    {/* Manage Button */}
                    <td style={{ ...getTdStyle(), textAlign: 'right', paddingRight: '16px' }}>
                      <ManageButton
                        workspace={workspace}
                        onMenuOpen={handleMenuOpen}
                        isMenuOpen={menuOpen && selectedWorkspace?.id === workspace.id}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Dropdown Menu */}
      {menuOpen && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPosition.top,
            left: menuPosition.left,
            minWidth: '240px',
            maxHeight: 'calc(100vh - 16px)',
            overflowY: 'auto',
            backgroundColor: 'var(--v2-surface)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-lg)',
            zIndex: 1000,
            animation: 'slideDown 0.1s ease',
          }}
        >
          {/* PLAN & BILLING */}
          <MenuSection label="Plan & Billing">
            <MenuItem
              icon={CreditCard}
              label="Subscription"
              onClick={() => {
                navigate(`/dashboard/workspace/${selectedWorkspace?.id}/subscription`);
                handleMenuClose();
              }}
            />
            {canAccessBilling && (
              <>
                <MenuItem
                  icon={Building2}
                  label="Billing Account"
                  onClick={() => {
                    setAssignBillingModalOpen(true);
                    handleMenuClose();
                  }}
                />
                <MenuItem
                  icon={Receipt}
                  label="Billing History"
                  onClick={() => {
                    navigate(`/dashboard/workspace/${selectedWorkspace?.id}/billing-history`);
                    handleMenuClose();
                  }}
                />
              </>
            )}
            {canManageBillingAdmins && (
              <MenuItem
                icon={Users}
                label="Billing Admins"
                onClick={() => {
                  setBillingAccessModalOpen(true);
                  handleMenuClose();
                }}
              />
            )}
          </MenuSection>

          <MenuDivider />

          {/* CONFIGURATION */}
          <MenuSection label="Configuration">
            {canAccessTechnical && (
              <>
<MenuItem
                  icon={Globe}
                  label="Custom Domains"
                  onClick={() => {
                    navigate(`/dashboard/workspace/${selectedWorkspace?.id}/domains`);
                    handleMenuClose();
                  }}
                />
                <MenuItem
                  icon={Shield}
                  label="IP Restrictions"
                  onClick={() => {
                    navigate(`/dashboard/workspace/${selectedWorkspace?.id}/ip-restrictions`);
                    handleMenuClose();
                  }}
                />
<MenuItem
                  icon={Mail}
                  label="SMTP Gateway"
                  onClick={() => {
                    navigate(`/dashboard/workspace/${selectedWorkspace?.id}/smtp`);
                    handleMenuClose();
                  }}
                />
                <MenuItem
                  icon={HardDrive}
                  label="Data Connections"
                  onClick={() => {
                    navigate(`/dashboard/workspace/${selectedWorkspace?.id}/data-connections`);
                    handleMenuClose();
                  }}
                />
                <MenuItem
                  icon={ArrowRightLeft}
                  label={getWorkspacePendingTransfer(selectedWorkspace?.id) ? 'Cancel Transfer' : 'Transfer Workspace'}
                  onClick={() => {
                    const pendingTransfer = getWorkspacePendingTransfer(selectedWorkspace?.id);
                    if (pendingTransfer) {
                      openCancelTransferModal(pendingTransfer.token, selectedWorkspace.name, pendingTransfer.target_email);
                    } else {
                      setTransferModalOpen(true);
                    }
                    handleMenuClose();
                  }}
                />
              </>
            )}
          </MenuSection>

          <MenuDivider />

          {/* ACCOUNT ACCESS */}
          <MenuSection label="Account Access">
            {canAccessTechnical && (
              <MenuItem
                icon={KeyRound}
                label="Reset Admin Password"
                onClick={() => {
                  setPasswordResetModalOpen(true);
                  handleMenuClose();
                }}
              />
            )}
          </MenuSection>

          <MenuDivider />

          {/* DELETE */}
          {canAccessTechnical && (
            <div style={{ padding: '4px 8px 8px' }}>
              <MenuItem
                icon={Trash2}
                label="Delete Workspace"
                danger
                onClick={() => {
                  setDeleteModalOpen(true);
                  handleMenuClose();
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateWorkspaceModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={loadWorkspaces}
      />

      <DeleteWorkspaceModal
        open={deleteModalOpen}
        workspace={selectedWorkspace}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={async (id) => {
          await apiService.delete(`/workspaces/${id}`);
          loadWorkspaces();
          setDeleteModalOpen(false);
        }}
      />

      {selectedWorkspace && (
        <>
          <DBCredentialsModal
            open={credentialsModalOpen}
            onClose={() => setCredentialsModalOpen(false)}
            workspaceId={selectedWorkspace.id}
          />
          <AdminPasswordResetModal
            open={passwordResetModalOpen}
            onClose={() => setPasswordResetModalOpen(false)}
            workspaceId={selectedWorkspace.id}
            workspaceName={selectedWorkspace.name}
            adminEmail={selectedWorkspace.customer_admin_email || ''}
          />
          <BillingAccessModal
            open={billingAccessModalOpen}
            workspaceId={selectedWorkspace.id}
            workspaceName={selectedWorkspace.name}
            onClose={() => setBillingAccessModalOpen(false)}
          />
          <AssignBillingProfileModal
            open={assignBillingModalOpen}
            onClose={() => setAssignBillingModalOpen(false)}
            workspaceId={selectedWorkspace.id}
            workspaceName={selectedWorkspace.name}
            currentBillingProfileId={selectedWorkspace.billing_profile_id || null}
            onAssigned={loadWorkspaces}
          />
          <TransferWorkspaceModal
            open={transferModalOpen}
            onClose={() => setTransferModalOpen(false)}
            workspaceId={selectedWorkspace.id}
            workspaceName={selectedWorkspace.name}
            isLifetime={selectedWorkspace.is_lifetime}
            hasActiveSubscription={selectedWorkspace.subscription_status === 'active'}
            onTransferInitiated={() => {
              loadPendingTransfers();
              loadWorkspaces();
            }}
          />
          
          <CancelTransferModal
            open={cancelTransferModalOpen}
            onClose={() => {
              setCancelTransferModalOpen(false);
              setCancelTransferData(null);
            }}
            token={cancelTransferData?.token || ''}
            workspaceName={cancelTransferData?.workspaceName || ''}
            targetEmail={cancelTransferData?.targetEmail || ''}
            onCancelled={handleCancelTransferComplete}
          />
        </>
      )}
    </div>
  );
};

// ============================================
// MANAGE BUTTON COMPONENT
// ============================================

const ManageButton: React.FC<{
  workspace: any;
  onMenuOpen: (event: React.MouseEvent<HTMLButtonElement>, workspace: any) => void;
  isMenuOpen: boolean;
}> = ({ workspace, onMenuOpen, isMenuOpen }) => {
  const [hovered, setHovered] = useState(false);

  return (
<button
      data-manage-btn
      onClick={(e) => onMenuOpen(e, workspace)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: hovered || isMenuOpen ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
        border: '1px solid var(--v2-border)',
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: 'var(--v2-text-secondary)',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      Manage
      <ChevronDown size={14} strokeWidth={1.5} />
    </button>
  );
};

// ============================================
// MENU COMPONENTS
// ============================================

const MenuSection: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ padding: '8px 8px 4px' }}>
    <div
      style={{
        fontSize: '0.6875rem',
        fontWeight: 500,
        color: 'var(--v2-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        padding: '4px 8px 8px',
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const MenuDivider: React.FC = () => (
  <div style={{ height: '1px', backgroundColor: 'var(--v2-border)', margin: '0' }} />
);

const MenuItem: React.FC<{
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  danger?: boolean;
}> = ({ icon: Icon, label, onClick, danger }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: '100%',
        padding: '8px 10px',
        backgroundColor: hovered ? (danger ? 'var(--v2-error-bg)' : 'var(--v2-surface-hover)') : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background-color 0.1s ease',
      }}
    >
      <Icon
        size={16}
        strokeWidth={1.5}
        style={{ color: danger ? 'var(--v2-error)' : 'var(--v2-text-tertiary)', flexShrink: 0 }}
      />
      <span
        style={{
          fontSize: '0.8125rem',
          fontWeight: 400,
          color: danger ? 'var(--v2-error)' : 'var(--v2-text)',
        }}
      >
        {label}
      </span>
    </button>
  );
};

export default WorkspacesPage;
